import { Injectable } from '@angular/core';
import { AuthService } from '@gelato-api-ui/core/auth/auth.service';
import { AssetProviderUid } from '@gelato-api-ui/core/designs/asset-provider-uid.enum';
import { FeatureSwitcherService } from '@gelato-api-ui/sdk/src/lib/feature-switcher/feature-switcher.service';
import { FeatureFlagEnum } from '@gelato-api-ui/sdk/src/lib/feature-switcher/featureFlagEnum';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { SubscriptionsFacade } from '../+state/subscriptions.facade';
import { PriceBreakdownFacade } from '../../shared/price-breakdown/price-breakdown.facade';
import { SubscriptionDiscountsFacade } from '@api-ui-app/src/app/shared/subscription-discounts/services/subscription-discounts.facade';
import { GelatoSubscriptionType } from '@gelato-api-ui/sdk/src/lib/shared/subscriptions-shared/constants/gelato-subscription.constant';
import { SubscriptionDiscount } from '@api-ui-app/src/app/shared/subscription-discounts/types/subscription-discounts';

@Injectable({ providedIn: 'root' })
export class SubscriptionsUserActionAvailabilityService {
  private isGuest$ = this.authService.isGuest();
  private isNotGuest$ = this.isGuest$.pipe(map(isGuest => !isGuest));

  showDesignFeeGelatoPlusUpsell$ = combineLatest([
    this.isNotGuest$,
    this.subscriptionsFacade.isPaidSubscriptionActive$,
  ]).pipe(map(([isNotGuest, gelatoPlusOrGoldIsActive]) => isNotGuest && !gelatoPlusOrGoldIsActive));
  showCatalogueGelatoPlusUpsell$ = combineLatest([
    this.subscriptionsFacade.isPaidSubscriptionActive$,
    this.featureSwitcherService.isFeatureEnabledForCurrentUser(FeatureFlagEnum.gelato_plus_upsell),
    this.isNotGuest$,
  ]).pipe(
    map(
      ([gelatoPlusOrGoldIsActive, isFeatureEnabled, isNotGuest]) =>
        isFeatureEnabled && !gelatoPlusOrGoldIsActive && isNotGuest,
    ),
  );
  showShutterstockGelatoPlusPrompt$ = combineLatest([
    this.isGuest$,
    this.subscriptionsFacade.isPaidSubscriptionActive$,
    this.subscriptionsFacade.isClientCreatedAfterGelatoPlusStart$,
  ]).pipe(
    map(
      ([isGuest, isGelatoPlusOrGoldActive, isClientCreatedAfterGelatoPlusStart]) =>
        !isGuest && !isGelatoPlusOrGoldActive && isClientCreatedAfterGelatoPlusStart,
    ),
  );
  showPriceBreakdownGelatoPlusPrompt$ = combineLatest([
    this.isGuest$,
    this.subscriptionsFacade.isPaidSubscriptionActive$,
    this.priceBreakdownFacade.designPriceBreakdown$,
  ]).pipe(
    map(([isGuest, isGelatoPlusOrGoldActive, designPriceBreakdown]) => {
      return (
        !isGuest &&
        !isGelatoPlusOrGoldActive &&
        designPriceBreakdown.filter(designItem =>
          this.getSubscriptionAvailableAssetProviders(designItem.assetProviderUid),
        )?.length > 0
      );
    }),
  );
  showEditorPluginsGelatoPlusPrompt$ = combineLatest([
    this.isGuest$,
    this.subscriptionsFacade.isPaidSubscriptionActive$,
  ]).pipe(
    map(([isGuest, isGelatoPlusOrGoldActive]) => {
      return !isGuest && !isGelatoPlusOrGoldActive;
    }),
  );

  constructor(
    private readonly authService: AuthService,
    private readonly subscriptionsFacade: SubscriptionsFacade,
    private readonly featureSwitcherService: FeatureSwitcherService,
    private readonly priceBreakdownFacade: PriceBreakdownFacade,
    private readonly subscriptionDiscountsFacade: SubscriptionDiscountsFacade,
  ) {}

  getConvertedGelatoPlusShippingDiscount(currency: string): Observable<SubscriptionDiscount> {
    return this.subscriptionDiscountsFacade.getConvertedShippingDiscountBySubscriptionType(
      GelatoSubscriptionType.GELATO_PLUS,
      currency,
    );
  }

  private getSubscriptionAvailableAssetProviders(assetProviderUid: AssetProviderUid): boolean {
    return [
      AssetProviderUid.CLIPART,
      AssetProviderUid.SHUTTERSTOCK_FULL,
      AssetProviderUid.SHUTTERSTOCK_ESSENTIALS,
      AssetProviderUid.GETTY_IMAGES,
    ].includes(assetProviderUid);
  }
}
